import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { findSection, isPendingSection, isVisibleSection } from '../lib/areas';
import type { WorkspaceSection } from '../lib/types';

export interface Crumb {
  id: string;
  display_name: string;
}

function ChildMenu({
  items, currentId, onClose,
}: {
  items: WorkspaceSection[];
  currentId: string | null;
  onClose: () => void;
}) {
  return (
    <div className="absolute top-full left-0 mt-2 navaris-dropdown min-w-[220px] max-w-[300px] max-h-[60vh] overflow-y-auto z-40 text-sm font-normal">
      {items.map(child => {
        const active = child.id === currentId;
        const pending = isPendingSection(child);
        return (
          <Link
            key={child.id}
            to={`/workspace/${child.id}`}
            onClick={onClose}
            aria-current={active ? 'page' : undefined}
            title={pending ? `${child.name} - answer the relevance questions to enable this section` : child.name}
            className={`flex items-center gap-2 px-4 py-1.5 transition-colors ${
              active ? 'bg-brand/10 text-brand-dark font-semibold' : 'text-graphite hover:bg-offwhite hover:text-brand-dark'
            } ${pending ? 'opacity-50' : ''}`}
          >
            <span className="truncate">{child.name}</span>
          </Link>
        );
      })}
    </div>
  );
}

// Breadcrumbs for a business area page. Where a crumb has subsections, the
// separator after it opens them, so every level can move sideways to a sibling;
// a trailing chevron after the last crumb offers the next level down.
export default function SectionBreadcrumbs({ crumbs }: { crumbs: Crumb[] }) {
  const { sections } = useWorkspace();
  const [openId, setOpenId] = useState<string | null>(null);
  const ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!openId) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpenId(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenId(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openId]);

  return (
    <h3 ref={ref} className="text-lg font-semibold flex items-center flex-wrap gap-1.5">
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        const children = findSection(sections, c.id)?.subsections?.filter(isVisibleSection) ?? [];
        const open = openId === c.id;
        const next = crumbs[i + 1]?.id ?? null;
        return (
          <span key={c.id} className="flex items-center gap-1.5">
            {isLast ? (
              <span className="text-graphite">{c.display_name}</span>
            ) : (
              <Link
                to={`/workspace/${c.id}`}
                className="text-coolslate hover:text-brand-dark transition-colors"
              >
                {c.display_name}
              </Link>
            )}
            {children.length > 0 ? (
              <span className="relative">
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : c.id)}
                  aria-expanded={open}
                  aria-haspopup="menu"
                  aria-label={`${c.display_name} sections`}
                  title={`${c.display_name} sections`}
                  className={`flex items-center rounded-md p-0.5 transition-colors hover:text-brand-dark hover:bg-white ${
                    open ? 'text-brand-dark bg-white' : 'text-coolslate/60'
                  }`}
                >
                  {open
                    ? <ChevronDown className="w-4 h-4" />
                    : <ChevronRight className="w-4 h-4" />}
                </button>
                {open && (
                  <ChildMenu items={children} currentId={next} onClose={() => setOpenId(null)} />
                )}
              </span>
            ) : (
              !isLast && <ChevronRight className="w-4 h-4 text-coolslate/60" />
            )}
          </span>
        );
      })}
    </h3>
  );
}
